import React from 'react';

import { Pill, Tooltip } from '@/ui';

import { policyDisplayName, truncateText } from '../../utils';

import type { PolicyIdentifier, Props } from './types';
import {
  POLICY_STATUS_COLORS,
  derivePolicyStatus,
  policyBannerFacts,
  policyIdentifiers,
  policyNameDetails,
  policyRailColor,
  policyRailLabel,
  policyStatusTitle
} from './utils';

// Long enough to recognise a carrier, short enough that the row never overflows.
const MAX_NAME_CHARS = 30;

const DetailList: React.FC<{ details: PolicyIdentifier[] }> = ({ details }) => (
  <div className="space-y-0.5 text-left">
    {details.map(({ label, value }) => (
      <div key={label}>
        <span className="opacity-70">{label}:</span> {value}
      </div>
    ))}
  </div>
);

const PolicyBanner: React.FC<Props> = ({ policyId, policy, roster }) => {
  const rail = policyRailColor(policyId, roster);
  const name = policy ? policyDisplayName(policy) : policyRailLabel(policyId, roster);

  return (
    <div
      className={`mb-3 flex items-center gap-1.5 overflow-hidden rounded px-2 py-1.5 ${rail.section}`}>
      <Tooltip content={policy ? <DetailList details={policyNameDetails(policy)} /> : name}>
        <Pill size="sm" className={`shrink-0 font-semibold ${rail.chip}`}>
          {truncateText(name, MAX_NAME_CHARS)}
        </Pill>
      </Tooltip>

      {policy && (
        <>
          {derivePolicyStatus(policy) !== 'Active' && (
            <Tooltip content={policyStatusTitle(policy)}>
              <Pill
                size="sm"
                color={POLICY_STATUS_COLORS[derivePolicyStatus(policy)]}
                className="shrink-0">
                {derivePolicyStatus(policy)}
              </Pill>
            </Tooltip>
          )}

          {policyBannerFacts(policy).map((fact) => (
            <Tooltip key={fact.title} content={fact.title}>
              <Pill size="sm" className="shrink-0 bg-white/80 whitespace-nowrap">
                {fact.text}
              </Pill>
            </Tooltip>
          ))}

          <Tooltip content={<DetailList details={policyIdentifiers(policy)} />}>
            <span className="shrink-0 px-1 text-xs text-gray-500">({policy.id.slice(0, 8)})</span>
          </Tooltip>
        </>
      )}
    </div>
  );
};

export default PolicyBanner;
